import { apiClient } from './apiClient';
import { Wallet, Transaction } from '../types';

/**
 * 钱包服务
 * 处理用户资产、充值、提现及交易历史
 */
class WalletService {
  /**
   * 格式化钱包数据，确保数值类型正确
   */
  private formatWallet(wallet: any): Wallet {
    if (!wallet) return wallet;
    return {
      ...wallet,
      balance: typeof wallet.balance === 'string' ? parseFloat(wallet.balance) : Number(wallet.balance) || 0,
    };
  }

  /**
   * 格式化交易记录，确保数值类型正确
   */
  private formatTransaction(tx: any): Transaction {
    if (!tx) return tx;
    return {
      ...tx,
      amount: typeof tx.amount === 'string' ? parseFloat(tx.amount) : Number(tx.amount) || 0,
    };
  }

  /**
   * 获取用户钱包列表
   */
  async getWallets(): Promise<Wallet[]> {
    const response = await apiClient.get<Wallet[]>('/wallets');
    if (!response.success) {
      throw new Error(response.message || '获取钱包列表失败');
    }
    return (response.data || []).map((w: any) => this.formatWallet(w));
  }

  /**
   * 获取用户主钱包 (USDT)
   */
  async getPrimaryWallet(
    currency: string = 'USDT',
    chain: string = 'POLYGON',
    sync: boolean = false,
  ): Promise<Wallet | null> {
    const response = await apiClient.get<Wallet>(`/wallets/primary`, {
      params: { currency, chain, sync },
    });
    // 注意：主钱包可能不存在，需要返回 null 而不是抛错，由调用方决定是否创建
    if (!response.success) {
      return null;
    }
    return this.formatWallet(response.data);
  }

  /**
   * 创建新钱包
   */
  async createWallet(currency: string = 'USDT', chain: string = 'POLYGON'): Promise<Wallet> {
    const response = await apiClient.post<Wallet>('/wallets/create', { currency, chain });
    if (!response.success) {
      throw new Error(response.message || '创建钱包失败');
    }
    return this.formatWallet(response.data);
  }

  /**
   * 删除钱包
   */
  async deleteWallet(id: number): Promise<void> {
    const response = await apiClient.delete(`/wallets/${id}`);
    if (!response.success) {
      throw new Error(response.error || '删除钱包失败');
    }
  }

  /**
   * 获取交易历史
   */
  async getTransactions(params?: {
    walletId?: number;
    type?: string;
    status?: string;
    limit?: number;
    sync?: boolean;
  }): Promise<Transaction[]> {
    const response = await apiClient.get<Transaction[]>('/wallets/transactions', { params });
    if (!response.success) {
      throw new Error(response.message || '获取交易历史失败');
    }
    return (response.data || []).map((tx: any) => this.formatTransaction(tx));
  }

  /**
   * 提交充值记录 (模拟)
   */
  async submitDeposit(walletId: string, amount: number, txHash?: string): Promise<Transaction> {
    const response = await apiClient.post<Transaction>('/wallets/transactions', {
      walletId,
      type: 'deposit',
      amount,
      currency: 'USDT',
      txHash,
    });
    if (!response.success) {
      throw new Error(response.message || '提交充值记录失败');
    }
    return this.formatTransaction(response.data);
  }

  /**
   * 提交提现请求 (模拟)
   */
  async submitWithdraw(walletId: string, amount: number, address: string): Promise<Transaction> {
    const response = await apiClient.post<Transaction>('/wallets/transactions', {
      walletId,
      type: 'withdraw',
      amount,
      currency: 'USDT',
      address,
    });
    if (!response.success) {
      throw new Error(response.message || '提交提现请求失败');
    }
    return this.formatTransaction(response.data);
  }
}

export const walletService = new WalletService();
